import { AlertModal } from '@/components/AlertModal';
import BaseLayout from '@/layout/BaseLayout';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useForm } from 'react-hook-form';

import { trpc } from '@/trpcClient';
import { checkBrandEditInput, type EditBrandInputType } from '@shelflix/schemas/brand/edit';
import Icon from '@shelflix/ui-web/components/Icon';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBrandFormStore } from './store';

import { ColorInput } from '@/components/ColorInput';
import { MultiSelect } from '@/components/MultiSelect';
import { useHasScopes } from '@/hooks/useHasScopes';
import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { logger } from '@/utils/logger';
import { usePreventLeave } from '@/utils/usePreventLeave';
import { urlToFile } from '@/utils/utils';

/**
 * Convert a File to base64 string (without data URL prefix)
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
import { allowedColors } from '@shelflix/schemas/_common/validation';
import { EBrandCategory, EBrandCategoryDisplayName } from '@shelflix/schemas/brand/enums';
import Dropzone from '@shelflix/ui-web/components/Dropzone/index';
import { toast } from 'sonner';

export const useListBrands = (search?: string) => {
  return useQuery({
    ...trpc.brand.list.queryOptions({
      search: { fields: ['code', 'name'], value: search ?? '' },
    }),
    enabled: !!search,
    refetchOnWindowFocus: false,
  });
};

export function BrandEdit() {
  const { uuid: brandUuid } = useParams<{ uuid: string }>();
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const canEditBrand = useHasScopes(['admin:brands:edit']);
  const canDeleteBrand = useHasScopes(['admin:brands:delete']);
  const uploadImageMutation = useMutation(trpc.image.uploadCompressed.mutationOptions());

  const migrateBrandMutation = useMutation(trpc.migration.brandMigration.mutationOptions());

  const { data: brandToEdit, isLoading: _isLoadingBrand } = useQuery(
    trpc.brand.get.queryOptions(
      {
        uuid: brandUuid ?? '',
      },
      {
        enabled: !!brandUuid,
      }
    )
  );
  const { formData, resetForm } = useBrandFormStore();

  const form = useForm<EditBrandInputType>({
    defaultValues: formData,
    mode: 'all',
  });
  const category = form.watch('category');
  const relatedBrands = form.watch('relatedBrands');

  useEffect(() => {
    if (brandToEdit) {
      urlToFile(brandToEdit.logoUrl, 'default').then((file) => {
        setThumbnail(file);

        form.reset({
          ...brandToEdit,
          relatedBrands: brandToEdit.relatedBrands?.map((b) => b.uuid),
          fileType: file?.type,
        });
      });
    }
  }, [brandToEdit]);

  const editBrandMutation = useMutation(trpc.brand.edit.mutationOptions());
  const deleteBrandMutation = useMutation(trpc.brand.delete.mutationOptions());

  const navigate = useNavigate();

  const confirmDelete = () => {
    if (!brandUuid) return;
    deleteBrandMutation.mutate(
      { uuid: brandUuid },
      {
        onSuccess: () => {
          toast.success('Marca excluída com sucesso');
          navigate('/brand');
        },
        onError: (error) => {
          toast.error(`Erro ao excluir marca: ${error.message}`);
        },
        onSettled: () => {
          setOpenDeleteModal(false);
        },
      }
    );
  };

  const onSubmit = async (data: EditBrandInputType) => {
    if (editBrandMutation.isPending || uploadImageMutation.isPending) return;

    try {
      setIsUploading(true);

      // First, upload the image with compression if there's a new thumbnail
      let imageKey: string | undefined;
      if (thumbnail) {
        const base64Data = await fileToBase64(thumbnail);
        const uploadResult = await uploadImageMutation.mutateAsync({
          base64Data,
          filename: thumbnail.name,
          imageType: 'brand-logo',
        });
        imageKey = uploadResult.key;
        logger.info('Image uploaded', {
          originalSize: uploadResult.originalSize,
          compressedSize: uploadResult.compressedSize,
          savingsPercent: uploadResult.savingsPercent,
        });
      }

      // Only send fields expected by the schema - exclude extra fields like logoUrl
      const payload: EditBrandInputType = {
        uuid: data.uuid,
        name: data.name,
        code: data.code.toUpperCase(),
        ledColor: data.ledColor,
        active: data.active,
        category: data.category,
        relatedBrands: data.relatedBrands ?? [],
        ...(imageKey && { imageKey }), // Use pre-uploaded key if available
      };

      await editBrandMutation.mutateAsync(payload);
      resetForm();
      navigate('/brand', { state: { toastSuccess: true } });
    } catch (error) {
      toast.error(`Erro ao editar marca: ${(error as Error).message}`);
    } finally {
      setIsUploading(false);
    }
  };
  usePreventLeave(isUploading);

  const dropzoneRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState<string | undefined>(undefined);
  const { data } = useListBrands(search);

  const brands = data?.brands ?? [];

  return (
    <BaseLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="w-full">
          <div className="flex w-full flex-col">
            <section className="flex w-full flex-col gap-[28px] rounded-[5px] sm:flex-row">
              <Card className="rounded-[10px] sm:order-1 sm:w-[50%]">
                <CardHeader className="pb-0">
                  <CardTitle className="text-primary text-[18px] leading-5 font-semibold sm:text-2xl">
                    Carregar logo
                  </CardTitle>
                </CardHeader>
                <CardContent className="bg-red-200">
                  <FormField
                    control={form.control}
                    name="fileType"
                    rules={{ validate: (value) => checkBrandEditInput('fileType', value) }}
                    render={({ field }) => (
                      <FormItem className="flex aspect-video flex-1 flex-col gap-[3px]">
                        <FormControl>
                          <Dropzone
                            removable={false}
                            onFilesSelected={(files) => {
                              setThumbnail(files[0]);
                              field.onChange(files[0]?.type);
                            }}
                            variant="video"
                            ref={dropzoneRef}
                            title="Arraste ou clique aqui para selecionar seu arquivo"
                            description="JPG, PNG ou SVG com fundo transparente (16:9)"
                            value={thumbnail ? [thumbnail] : undefined}
                            readonly={!canEditBrand}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
              <Card className="order-1 flex rounded-[10px] sm:order-2 sm:w-[50%]">
                <CardHeader className="pb-0">
                  <CardTitle className="text-primary text-[18px] leading-5 font-semibold sm:text-2xl">
                    Informações da marca
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-[27px_12px]">
                  <FormField
                    control={form.control}
                    name="name"
                    rules={{ validate: (value) => checkBrandEditInput('name', value) }}
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[3px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Nome da Marca</FormLabel>
                        <FormControl>
                          <Input
                            className="h-10"
                            placeholder="Digite o nome da marca"
                            disabled={!canEditBrand}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="code"
                    rules={{ validate: (value) => checkBrandEditInput('code', value) }}
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[3px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                          Identificador de 3 dígitos
                        </FormLabel>
                        <FormControl>
                          <Input className="h-10 uppercase" disabled={!canEditBrand} {...field} maxLength={3} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="active"
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[3px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Status</FormLabel>
                        <FormControl>
                          <Select
                            value={String(field.value)}
                            onValueChange={(val) => field.onChange(val === 'true')}
                            disabled={!canEditBrand}
                          >
                            <SelectTrigger className="h-10 w-full">
                              <SelectValue placeholder="Selecione uma opção" />
                            </SelectTrigger>
                            <SelectContent className=" ">
                              <SelectItem value={'true'}>Ativa</SelectItem>
                              <SelectItem value={'false'}>Inativa</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="category"
                    rules={{ validate: (value) => checkBrandEditInput('category', value) }}
                    render={({ field }) => (
                      <FormItem className="flex w-full flex-col gap-[3px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Categoria</FormLabel>
                        <FormControl>
                          <Select
                            defaultValue={field.value ?? undefined}
                            value={field.value ?? undefined}
                            onValueChange={(category: EBrandCategory) => {
                              field.onChange(category);
                              if (category !== EBrandCategory.MULTIBRAND) {
                                form.setValue('relatedBrands', []);
                              } else {
                                form.setValue('relatedBrands', brandToEdit?.relatedBrands?.map((b) => b.uuid) ?? []);
                              }
                              form.trigger('relatedBrands');
                            }}
                            disabled={!canEditBrand}
                          >
                            <SelectTrigger className="h-10 w-full">
                              <SelectValue placeholder="Selecione uma opção" />
                            </SelectTrigger>
                            <SelectContent className=" ">
                              {Object.values(EBrandCategory).map((category) => (
                                <SelectItem key={category} value={category}>
                                  {EBrandCategoryDisplayName[category]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="ledColor"
                    rules={{ validate: (value) => checkBrandEditInput('ledColor', value) }}
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[3px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                          Código de cor - LED
                        </FormLabel>
                        <FormControl>
                          <ColorInput
                            value={field.value}
                            onChange={field.onChange}
                            mode="select"
                            options={allowedColors}
                            disabled={!canEditBrand}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {category === EBrandCategory.MULTIBRAND && (
                    <FormField
                      control={form.control}
                      name="relatedBrands"
                      rules={{
                        validate: (value) => checkBrandEditInput('relatedBrands', value ?? [], form.getValues()),
                      }}
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-[3px]">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                            Marcas Vinculadas
                          </FormLabel>
                          <FormControl>
                            <MultiSelect
                              onSearch={setSearch}
                              onSelect={(item) => field.onChange([...(relatedBrands ?? []), item.value])}
                              onRemove={(item) => field.onChange(relatedBrands?.filter((i) => i !== item.value) ?? [])}
                              initialValue={
                                brandToEdit?.relatedBrands?.map((b) => ({ text: b.name, value: b.uuid })) ?? []
                              }
                              items={
                                brands
                                  ?.filter((brand) => !relatedBrands?.includes(brand.uuid) && brand.uuid !== brandUuid)
                                  .map((brand) => ({ text: brand.name, value: brand.uuid })) ?? []
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </CardContent>
              </Card>
            </section>
            <div className="my-5 flex grow-0 justify-center gap-4">
              <RestrictedComponent requiredScopes={['admin:brands:delete']}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpenDeleteModal(true)}
                  className="flex cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold"
                >
                  <Icon name="delete" fill={1} className="text-primary" size={16} />
                  <span className="text-primary [font-family:'Figtree'] text-sm">Excluir marca</span>
                </Button>
              </RestrictedComponent>
              <RestrictedComponent requiredScopes={['admin:brands:edit']}>
                <Button
                  type="submit"
                  loading={editBrandMutation.isPending || isUploading}
                  className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-auto sm:w-fit sm:grow-0"
                >
                  <Icon name="label" fill={1} color="#fff" size={16} />
                  <span className="[font-family:'Figtree'] text-sm">Salvar marca</span>
                </Button>
              </RestrictedComponent>
              {brandToEdit?.externalId === null && (
                <Button
                  disabled={migrateBrandMutation.isPending}
                  className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-fit sm:grow-0"
                  onClick={(e) => {
                    e.preventDefault();
                    migrateBrandMutation.mutate(
                      {
                        brandUuid: brandUuid || '',
                      },
                      {
                        onSuccess: () => {
                          toast.success('Migração realizada com sucesso');
                        },
                        onError: (error) => {
                          toast.error(`Erro ao realizar migração: ${error.message}`);
                        },
                      }
                    );
                  }}
                >
                  <span className="font-['Figtree'] text-sm">Migrar Marca</span>
                </Button>
              )}
            </div>
          </div>
        </form>
      </Form>

      {canDeleteBrand && (
        <AlertModal
          openModal={openDeleteModal}
          onOpenChange={setOpenDeleteModal}
          icon={<Icon name="delete" className="text-primary" size={40} fill={1} />}
          title="Atenção! Confirme esta ação"
          subtitle="Clique no botão de excluir para deletar essa marca"
          content={
            <p className="font-[Figtree] text-base text-[#DC3545]">
              Esta não é uma ação reversível. Ao clicar em excluir, a marca será deletada da nossa base de dados. Caso
              essa marca esteja associada a materiais, campanhas, mídias ou seja parte de uma multimarca, a operação irá
              falhar.
            </p>
          }
          actionBotton={
            <div className="flex flex-row gap-2">
              <Button
                variant="outline"
                onClick={confirmDelete}
                loading={deleteBrandMutation.isPending}
                className="w-1/2"
              >
                <Icon name="delete" className="text-primary" fill={1} />
                <span className="text-primary">Excluir</span>
              </Button>

              <Button onClick={() => setOpenDeleteModal(false)} className="w-1/2">
                <Icon name="close" className="text-white" fill={1} />
                <span className="text-white">Cancelar</span>
              </Button>
            </div>
          }
        />
      )}
    </BaseLayout>
  );
}
